/**
 * Pantalla de inicio — el panel del negocio.
 *
 * Es lo primero que se ve cada mañana, y responde en este orden:
 *
 *   1. **¿Cómo va el día?** Vendido, ventas, efectivo en el cajón y ganancia,
 *      comparado con ayer. Una cifra sola no dice nada: "vendí 120" no es
 *      información hasta que sabes que ayer vendiste 90.
 *   2. **¿Hay algo urgente?** Tasas viejas y productos agotados. La tasa vieja
 *      es el error más caro de un negocio multimoneda: vender todo el día con
 *      la tasa de la semana pasada es regalar dinero.
 *   3. **¿Qué falta por configurar?** Con enlaces directos.
 *
 * El bloque de ganancia solo se PIDE al servidor si el rol puede verlo. No es
 * solo esconderlo: si se pidiera, el servidor respondería 403 y el empleado
 * vería un error rojo sin entender por qué.
 */

import { Link, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Banknote,
  Check,
  Coins,
  GraduationCap,
  Package,
  Receipt,
  Scissors,
  TrendingUp,
  Wallet,
} from "lucide-react";
import {
  Boton,
  Insignia,
  Tarjeta,
  TituloSeccion,
  cx,
} from "@/components/ui";
import {
  MONEDA_POR_DEFECTO,
  comoFormato,
  formatMoney,
  explicarTasa,
  formatRate,
  monedaPrincipal,
  formatPercent,
  toNumber,
} from "@/lib/money";
import { leerTasa } from "@/lib/direccionDeTasa";
import {
  catalogo,
  equipo as endpointEquipo,
  fiado,
  inventario,
  monedas,
  negocio,
  porPagar,
  reportes,
} from "@/lib/endpoints";
import { useAuth } from "@/stores/auth";
import { TasaDelDia } from "@/features/monedas/TasaDelDia";
import { useSucursal } from "@/stores/sucursal";
import { useTutorial } from "@/stores/tutorial";
import { BarrasVerticales } from "@/features/reportes/Grafico";
import { plural } from "@/lib/texto";
import { Atencion, type AsuntoPendiente } from "./Atencion";
import { CifraDelDia } from "./CifraDelDia";
import { PorCobrarPronto, PorPagarPronto } from "./CuentasPendientes";
import { GastosDeInicio } from "./GastosDeInicio";
import { ImpuestosDeInicio } from "./ImpuestosDeInicio";
import { UtilidadVsFacturacion } from "./UtilidadVsFacturacion";
import { DeLaSucursal, useSucursales } from "@/components/SucursalActual";
import { BotonDeAyuda } from "@/features/ayuda/BotonDeAyuda";

/**
 * En un almacén, «Inicio» es su inventario (2026-10-01): el resumen de aquí
 * habla de ventas, y en un almacén no se vende. Ver `ALMACEN` en `navegacion.tsx`.
 */
export function InicioPage() {
  const { actual } = useSucursales();
  if (actual?.tipo === "almacen") return <Navigate to="/inventario" replace />;
  return <InicioDeLaSucursal />;
}

function InicioDeLaSucursal() {
  const { user, tenant, puede } = useAuth();
  const qc = useQueryClient();

  // El panel del día. Solo para quien puede ver reportes; un empleado entra a
  // Inicio y ve las tasas y los avisos, pero no el consolidado del negocio.
  const { data: panel } = useQuery({
    queryKey: ["panel"],
    queryFn: reportes.panel,
    enabled: puede("reports.view"),
    // Medio minuto: al volver a Inicio después de vender, la cifra ya está al día
    // sin machacar al servidor con una consulta por cada cambio de pantalla.
    staleTime: 30_000,
  });

  const { data: misMonedas } = useQuery({
    queryKey: ["monedas-negocio"],
    queryFn: monedas.listar,
    enabled: puede("currency.view"),
  });

  const { data: productos } = useQuery({
    queryKey: ["items", { kind: "product", size: 1 }],
    queryFn: () => catalogo.listar({ kind: "product", size: 1 }),
    enabled: puede("catalog.view"),
  });

  const { data: servicios } = useQuery({
    queryKey: ["items", { kind: "service", size: 1 }],
    queryFn: () => catalogo.listar({ kind: "service", size: 1 }),
    enabled: puede("catalog.view"),
  });

  // El de la sucursal elegida, como en Inventario (ver `stock_summary`).
  const sucursalElegida = useSucursal((s) => s.porNegocio[tenant?.id ?? ""] ?? null);
  const { data: inv } = useQuery({
    queryKey: ["inventario-resumen", sucursalElegida],
    queryFn: inventario.resumen,
    enabled: puede("inventory.view"),
  });

  /*
    Las tres consultas de la mitad de abajo.

    ⚠️ `enabled` por permiso, no `if` en el render. Pedirlas siempre y esconder
    el resultado significa que un cajero dispara tres peticiones que el servidor
    le va a rechazar con un 403 — ruido en los registros y tiempo de carga
    regalado en el aparato más lento del local.
  */
  const { data: porPagarResumen } = useQuery({
    queryKey: ["por-pagar-resumen"],
    queryFn: () => porPagar.listar(),
    enabled: puede("reports.view_costs"),
  });

  const { data: porCobrarResumen } = useQuery({
    queryKey: ["por-cobrar-resumen"],
    queryFn: () => fiado.cuentasPorCobrar(),
    enabled: puede("pos.sell"),
  });

  const { data: ganancia30 } = useQuery({
    queryKey: ["ganancia-30dias"],
    queryFn: () => reportes.ganancia({ periodo: "30dias" }),
    enabled: puede("reports.view_costs"),
  });

  const { data: datosNegocio } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 5 * 60_000,
  });

  const { data: equipo } = useQuery({
    queryKey: ["equipo"],
    queryFn: endpointEquipo.listar,
    enabled: puede("users.view"),
  });

  /**
   * Marca un paso como "no aplica a mi negocio".
   *
   * Se guarda en los ajustes del NEGOCIO y no en el navegador: si el dueño
   * entra desde su celular, tampoco debería volver a ver el paso que ya
   * descartó desde la computadora.
   */
  const ocultarPaso = useMutation({
    mutationFn: (clave: "team_skipped") => negocio.actualizarAjustes({ [clave]: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["negocio"] }),
  });

  const equipoPendiente =
    puede("users.manage") &&
    !(datosNegocio?.settings?.team_skipped ?? false) &&
    (equipo?.length ?? 1) <= 1;

  const principalDelNegocio = misMonedas ? monedaPrincipal(misMonedas) : undefined;
  const formatoPrincipal = principalDelNegocio
    ? comoFormato(principalDelNegocio)
    : MONEDA_POR_DEFECTO;

  const secundarias = (misMonedas ?? []).filter((m) => !m.is_primary && m.is_active);
  // ⚠️ La del BCV se pone sola y no sale los fines de semana ni los feriados:
  // un sábado ya tiene «1 día» sin que nadie deba hacer nada. Se avisa solo si
  // pasa de 4 días, que es cuando algo falló (la misma regla de `TasaDelDia`).
  // La escrita a mano, al día siguiente.
  const tasasViejas = secundarias.filter((m) =>
    m.tasa_automatica ? (m.rate_age_days ?? 0) >= 4 : (m.rate_age_days ?? 0) >= 1,
  );
  const totalItems = (productos?.total ?? 0) + (servicios?.total ?? 0);

  /*
    ═══════════════════════════════════════════════════════════════════════
     LO QUE HAY QUE MIRAR HOY, EN UNA SOLA LISTA
    ═══════════════════════════════════════════════════════════════════════

    Se juntan aquí para que la tarjeta pueda decir «3 cosas por mirar» y
    ordenarlas por urgencia. Antes cada aviso se pintaba por su cuenta y nadie
    sabía cuántos había hasta contarlos con el dedo en la pantalla.
  */
  const asuntos: AsuntoPendiente[] = [];

  if (tasasViejas.length > 0) {
    const uno = tasasViejas[0]!;
    asuntos.push({
      id: "tasas",
      icono: <TrendingUp size={15} />,
      // ⚠️ URGENTE de verdad: con una tasa vieja se cobra de menos en CADA
      // venta, y en un país con inflación eso es dinero perdido cada hora.
      urgente: true,
      titulo:
        tasasViejas.length === 1 ? (
          <>
            La tasa de <strong>{uno.currency_code}</strong> lleva{" "}
            {plural(uno.rate_age_days ?? 0, "día", "días")} sin actualizarse
          </>
        ) : (
          <>
            <strong>{tasasViejas.length} monedas</strong> tienen la tasa desactualizada
          </>
        ),
      detalle: "Si vendes con una tasa vieja, estás cobrando de menos.",
      a: "/monedas",
      accion: "Actualizar",
    });
  }

  if (inv && inv.out_of_stock + inv.low_stock > 0) {
    const partes: string[] = [];
    if (inv.out_of_stock > 0)
      partes.push(`${inv.out_of_stock} ${inv.out_of_stock === 1 ? "agotado" : "agotados"}`);
    if (inv.low_stock > 0) partes.push(`${inv.low_stock} por debajo del mínimo`);
    asuntos.push({
      id: "inventario",
      icono: <Package size={15} />,
      titulo: (
        <>
          <strong>{partes.join(" y ")}</strong>
        </>
      ),
      detalle: "Revisa qué hay que comprar antes de que te lo pidan.",
      a: "/inventario",
      accion: "Ver inventario",
    });
  }

  const quedaAlgoPorHacer =
    (puede("catalog.manage") && totalItems === 0) ||
    (puede("currency.manage") && secundarias.length === 0) ||
    equipoPendiente;

  return (
    <div className="space-y-5">
      {/* ---------- Saludo, y la tasa del día a su lado ---------- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="mr-auto min-w-0">
          <h1 className="titulo-pagina">
            {saludo()}
            {user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-sm text-muted">
              {tenant?.name} · precios en {tenant?.primary_currency_code}
            </p>
            {/* Las cifras de Inicio son de la sucursal elegida (2026-09-28). */}
            <DeLaSucursal />
          </div>
        </div>
        {/*
          La tasa arriba, a la vista (2026-10-02): «Tasas de hoy» está al final
          de la página y el dueño pidió verla sin buscarla.
        */}
        {misMonedas && <TasaDelDia monedas={misMonedas} variante="inicio" />}
        <BotonDeAyuda />
      </div>

      {/* ---------- Invitación al recorrido guiado ---------- */}
      <InvitacionTutorial />

      {/*
        ═════════════════════════════════════════════════════════════════════
         EL ORDEN DE ESTA PANTALLA NO ES CASUAL
        ═════════════════════════════════════════════════════════════════════

          1. Las CIFRAS del día. Es a lo que se entra: «¿cómo voy?».
          2. Lo que hay que MIRAR. Va justo debajo, todavía sin desplazar.
          3. El DETALLE (gráfica y lo más vendido), para quien quiera más.

        Los avisos estaban al final, después de las gráficas, y ahí no los ve
        nadie: quien mira las cuatro cifras y se va, se va sin enterarse de que
        tiene la tasa vieja. Y arriba del todo tampoco: tapar el dinero con una
        advertencia cada mañana es la forma de que se aprenda a saltarla.
      */}
      {panel && <CifrasDelDia panel={panel} monedas={misMonedas ?? []} />}

      <Atencion asuntos={asuntos} />

      {panel && <DetalleDelDia panel={panel} monedas={misMonedas ?? []} />}

      {/*
        ---------- Lo que debes y lo que te deben ----------

        Van JUNTAS y en la misma fila a propósito: son las dos caras de la misma
        pregunta —«¿con qué cuento de aquí a fin de mes?»— y separarlas obliga a
        recordar una mientras se lee la otra.
      */}
      {(puede("reports.view_costs") || puede("pos.sell")) && (
        <div
          className={cx(
            "grid gap-4",
            puede("reports.view_costs") && puede("pos.sell") && "lg:grid-cols-2",
          )}
        >
          {puede("reports.view_costs") && (
            <PorPagarPronto resumen={porPagarResumen} formato={formatoPrincipal} />
          )}
          {puede("pos.sell") && (
            <PorCobrarPronto resumen={porCobrarResumen} formato={formatoPrincipal} />
          )}
        </div>
      )}

      {/* ---------- Gastos: hoy, el mes y los últimos, con su caja ---------- */}
      {puede("cash.movement") && <GastosDeInicio formato={formatoPrincipal} />}

      {/* ---------- El IVA: hoy, el mes y cada impuesto (2026-10-20) ---------- */}
      {puede("reports.resumen") && (
        <ImpuestosDeInicio
          formato={formatoPrincipal}
          impuestosActivos={Boolean(datosNegocio?.settings?.taxes_enabled)}
        />
      )}

      {/* ---------- Utilidad vs facturación ---------- */}
      {ganancia30 && ganancia30.por_dia.length > 1 && (
        <UtilidadVsFacturacion dias={ganancia30.por_dia} formato={formatoPrincipal} />
      )}

      {/*
        ═════════════════════════════════════════════════════════════════════
         LA FRANJA DE ABAJO: TASAS Y CATÁLOGO, LADO A LADO
        ═════════════════════════════════════════════════════════════════════

        Antes eran dos bloques a todo lo ancho, uno debajo del otro. «Tasas de
        hoy» ocupaba una franja entera para enseñar UNA ficha de moneda, y
        debajo otra franja con tres números que casi nunca cambian.

        Juntos en dos columnas ocupan la mitad de alto y dejan a la vista lo que
        de verdad se mira a diario, que está arriba. No es apretar por apretar:
        son dos cosas de consulta, y las cosas de consulta no merecen el ancho
        completo de la pantalla.
      */}
      <div
        className={cx(
          "grid gap-4",
          // Mismo motivo que arriba: sin monedas secundarias, «Tu catálogo» se
          // queda solo y tiene que ocupar el ancho entero.
          puede("currency.view") && secundarias.length > 0 && "lg:grid-cols-2",
        )}
      >
        {puede("currency.view") && secundarias.length > 0 && (
          <Tarjeta>
            <TituloSeccion
              titulo="Tasas de hoy"
              descripcion={`Cuánto vale 1 ${tenant?.primary_currency_code} en cada moneda que cobras.`}
              accion={
                puede("currency.manage") ? (
                  <Link to="/monedas">
                    <Boton variante="secundario" tamano="sm" icono={<TrendingUp size={15} />}>
                      Actualizar
                    </Boton>
                  </Link>
                ) : undefined
              }
            />

            <div className="grid gap-2.5 sm:grid-cols-2">
              {secundarias.map((m) => {
                const dias = m.rate_age_days ?? 0;
                return (
                  <div
                    key={m.currency_code}
                    className={cx(
                      "rounded-[var(--radius-control)] border p-3.5",
                      dias >= 1 ? "border-warning/40 bg-warning-soft" : "border-line bg-surface-2",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-muted">{m.currency_code}</span>
                      {dias >= 1 ? (
                        <Insignia tono="aviso">hace {dias}d</Insignia>
                      ) : (
                        <Insignia tono="exito">hoy</Insignia>
                      )}
                    </div>
                    {/*
                      ⚠️ El número grande es el de la frase, no el guardado.

                      Con una moneda principal «grande» —el peso colombiano— lo
                      guardado es 0,0002439024, y en un titular de veinte
                      píxeles eso no es un dato: es un borrón. Lo que hay que
                      leer de un vistazo desde la puerta es «4.100».
                    */}
                    <p className="tabular mt-1.5 text-xl font-semibold text-ink">
                      {formatRate(
                        leerTasa(m.current_rate, tenant?.primary_currency_code ?? "", m.currency_code)
                          ?.cuanto ?? m.current_rate,
                      )}
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted">
                      {explicarTasa(m.current_rate, tenant?.primary_currency_code ?? "", m.currency_code)}
                    </p>
                  </div>
                );
              })}
            </div>
          </Tarjeta>
        )}

        {/* ---------- Lo que tienes cargado ---------- */}
        <Tarjeta>
          <TituloSeccion
            titulo="Tu catálogo"
            descripcion="Lo que Softfi sabe vender. Toca uno para abrirlo."
          />
          <div className="grid gap-2.5 sm:grid-cols-3">
            <Cifra
              icono={<Package size={18} />}
              etiqueta="Productos"
              valor={productos?.total ?? 0}
              a="/catalogo?tipo=product"
            />
            <Cifra
              icono={<Scissors size={18} />}
              etiqueta="Servicios"
              valor={servicios?.total ?? 0}
              a="/catalogo?tipo=service"
            />
            <Cifra
              icono={<Coins size={18} />}
              etiqueta="Monedas activas"
              valor={(misMonedas ?? []).filter((m) => m.is_active).length}
              a="/monedas"
            />
          </div>
        </Tarjeta>
      </div>

      {/*
        ---------- Qué falta por configurar ----------

        La tarjeta DESAPARECE cuando no queda nada pendiente. Una lista con todo
        tachado ocupa media pantalla para no decir nada, y encima empuja hacia
        abajo las cifras del día, que es a lo que se entra de verdad.
      */}
      {quedaAlgoPorHacer && (
      <Tarjeta>
        <TituloSeccion
          titulo="Prepara tu negocio"
          descripcion="Completa estos pasos y Softfi queda listo para vender."
        />

        <div className="divide-y divide-line">
          <Paso
            hecho={totalItems > 0}
            titulo="Carga tus productos y servicios"
            descripcion="Lo que vendes, con su precio. Puedes empezar con 5 y seguir después."
            a="/catalogo"
            visible={puede("catalog.manage")}
          />
          <Paso
            hecho={secundarias.length > 0}
            titulo="Habilita las monedas en las que cobras"
            descripcion="Bolívares, pesos, euros… cada una con su tasa del día."
            a="/monedas"
            visible={puede("currency.manage")}
          />
          {/*
            El paso del equipo se marca solo cuando de verdad hay más de una
            persona. Antes miraba `onboarding_completed`, que no tiene nada que
            ver: se marcaba como hecho por haber terminado el wizard aunque el
            negocio siguiera siendo de una sola persona.
          */}
          <Paso
            hecho={(equipo?.length ?? 1) > 1}
            titulo="Invita a tu equipo"
            descripcion="Cada empleado con su usuario. Los cajeros no ven costos ni tasas."
            a="/equipo"
            visible={puede("users.manage") && !(datosNegocio?.settings?.team_skipped ?? false)}
            descartar={{
              texto: "Trabajo solo, no necesito esto",
              onDescartar: () => ocultarPaso.mutate("team_skipped"),
            }}
          />
        </div>
      </Tarjeta>
      )}

    </div>
  );
}

// =============================================================================
//  INVITACIÓN AL RECORRIDO
// =============================================================================

/**
 * La tarjeta que ofrece el tutorial.
 *
 * Solo aparece si NO se ha terminado el recorrido. Después desaparece sola y no
 * vuelve: el tutorial sigue estando en Ajustes para quien lo quiera repetir,
 * pero un aviso permanente en la pantalla de inicio se convierte en ruido que
 * el usuario aprende a ignorar — y entonces deja de ver también los avisos que
 * sí importan, como la tasa desactualizada.
 */
function InvitacionTutorial() {
  const terminado = useTutorial((e) => e.terminado);
  const aMedias = useTutorial((e) => e.aMedias);
  const abrir = useTutorial((e) => e.abrir);
  const terminar = useTutorial((e) => e.terminar);

  if (terminado) return null;

  return (
    /*
      ⚠️ EN CELULAR SE APILA, Y NO ES UN DETALLE.

      Estaba en `flex flex-wrap items-center`. En una pantalla de 375 px el
      icono (44) más los dos botones (~200) más los huecos se comían casi todo,
      y al texto le quedaban unos 60 px: salía UNA PALABRA POR LÍNEA. `flex-1`
      con `min-w-0` deja encoger hasta lo absurdo antes de partir la fila, así
      que `flex-wrap` no llegaba a actuar nunca.

      Apilado en vertical hasta `sm`, el texto tiene el ancho entero y los
      botones van debajo, que es donde el pulgar los alcanza.
    */
    <div className="flex flex-col items-start gap-3 rounded-[var(--radius-card)] border border-accent/30 bg-accent-soft p-4 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex size-11 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-accent text-on-accent">
        <GraduationCap size={20} />
      </div>

      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink">
          {aMedias ? "¿Retomamos el recorrido?" : "¿Primera vez con Softfi?"}
        </p>
        <p className="mt-0.5 text-sm text-muted">
          {aMedias
            ? "Lo dejaste a medias. Te quedan un par de minutos."
            : "Te enseño lo esencial en un minuto: vender, cobrar, cuadrar la caja y ver cuánto ganaste."}
        </p>
      </div>

      <div className="flex w-full shrink-0 gap-2 sm:w-auto">
        <Boton variante="fantasma" tamano="sm" onClick={terminar}>
          No, gracias
        </Boton>
        <Boton tamano="sm" onClick={() => abrir(!aMedias)}>
          {aMedias ? "Continuar" : "Empezar"}
        </Boton>
      </div>
    </div>
  );
}

// =============================================================================
//  EL PANEL DEL DÍA
// =============================================================================

/**
 * Las cifras de hoy, con la comparación contra ayer.
 *
 * Tres decisiones que hacen que se lea de un vistazo:
 *
 *   1. **La comparación va pegada a la cifra**, no en otra tarjeta. "+34% vs
 *      ayer" solo significa algo justo al lado del número al que se refiere.
 *   2. **El color solo aparece cuando dice algo**: verde si vendiste más que
 *      ayer, rojo si menos. Colorear todo convierte el color en decoración y
 *      deja de avisar de nada.
 *   3. **El efectivo esperado sale aquí** porque es la pregunta de las 6 de la
 *      tarde: "¿cuánto debería tener en el cajón?".
 */
/** Las cuatro cifras del día: lo primero que se mira al abrir. */
function CifrasDelDia({
  panel,
  monedas: listaMonedas,
}: {
  panel: import("@/lib/types").Panel;
  monedas: import("@/lib/types").TenantCurrency[];
}) {
  const principal = monedaPrincipal(listaMonedas);
  const formato = principal ? comoFormato(principal) : MONEDA_POR_DEFECTO;

  /*
   * La comparación con ayer SOLO se muestra si ya hubo alguna venta hoy.
   *
   * Sin esto, a las 8 de la mañana el panel saluda con un "−100% vs ayer" en
   * rojo. Es matemáticamente cierto y completamente inútil: todavía no ha
   * abierto el negocio. Alarmar a alguien con un dato que solo dice "aún no has
   * empezado" es la forma más rápida de que deje de mirar el panel.
   */
  const variacion = panel.ventas_hoy > 0 ? panel.comparacion.variacion : null;

  return (
    /*
      Dos por fila ya en el celular. Con una sola columna, las cuatro cifras
      del día ocupaban pantalla y media: había que desplazar para ver la
      ganancia, que es justo la que más se busca. La pregunta «¿cómo voy?» se
      contesta de un vistazo o no se contesta.
    */
    <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4">
      {/*
          ═══════════════════════════════════════════════════════════════════
           ⚠️ «ENTRÓ» VA PRIMERO, Y «FACTURADO» AL LADO. NO ES UN DETALLE.
          ═══════════════════════════════════════════════════════════════════

          Antes había una sola tarjeta grande, «Facturado hoy», y debajo en
          letra chica el reparto entre lo cobrado y lo fiado. El dueño lo dijo
          con un ejemplo que no admite discusión:

            «Un negocio hace su primera venta del día, 30 dólares, pero fue a
             crédito y sin inicial. En el inicio sale 30 y eso está mal.»

          Y está mal. La cifra grande es la que se lee de lejos y la que se
          recuerda; poner ahí lo facturado y relegar lo cobrado a un renglón de
          once píxeles es enseñar el número equivocado con una nota al pie.

          Son dos preguntas distintas y las dos importan:

            «¿Cuánto entró?»  → el bolsillo. Con eso se repone mañana.
            «¿Cuánto vendí?»  → el mostrador. Mide cómo fue el día.

          Así que van dos tarjetas del mismo tamaño, y la del dinero va
          primero. El contador de ventas se muda al detalle de «Facturado»,
          que es donde pertenece: es el mismo dato.
        */}
        <CifraDelDia
          rotulo="Ingresos de hoy"
          valor={formatMoney(panel.entro_hoy, formato)}
          icono={<Wallet size={16} />}
          tono={toNumber(panel.entro_hoy) > 0 ? "exito" : "neutro"}
          detalle={
            toNumber(panel.abonos_hoy) > 0 ? (
              <span>
                <strong className="text-ink">{formatMoney(panel.cobrado_hoy, formato)}</strong> de
                ventas de hoy · {formatMoney(panel.abonos_hoy, formato)} de deudas cobradas
              </span>
            ) : (
              "Dinero de verdad, sin lo que quedó a crédito"
            )
          }
        />

        <CifraDelDia
          rotulo="Facturado hoy"
          valor={formatMoney(panel.total_hoy, formato)}
          icono={<Receipt size={16} />}
          variacion={variacion}
          detalle={
            toNumber(panel.fiado_hoy) > 0 ? (
              <span className="text-warning">
                {formatMoney(panel.fiado_hoy, formato)} de esto quedó a crédito
              </span>
            ) : (
              `${panel.ventas_hoy} ${panel.ventas_hoy === 1 ? "venta" : "ventas"} hoy`
            )
          }
        />

        {/*
          ⚠️ EL CAJÓN NO SE LE ENSEÑA A CUALQUIERA — CIERRE CIEGO.

          Al cajero no se le dice cuánto DEBE haber: sabiéndolo puede contar 18,
          ver que se esperan 23 y escribir 23, y el faltante queda tapado hasta
          que otro cuente el dinero. La pantalla de Caja ya lo escondía; este
          panel lo servía sin pedir nada.

          Cuando no se puede enseñar, en su lugar va lo vendido en la semana:
          un dato útil, del mismo tamaño, y que no deja un hueco raro.
        */}
        {panel.efectivo_esperado !== null ? (
          /*
            El efectivo del negocio: las cajas abiertas más el resguardo
            (2026-10-13), la misma cifra que la cuenta Efectivo. Antes era solo
            el cajón, y abrir una caja con fondo cero lo dejaba en cero.
          */
          <CifraDelDia
            rotulo="Efectivo disponible"
            valor={formatMoney(panel.efectivo_esperado, formato)}
            icono={<Banknote size={16} />}
            detalle={
              panel.efectivo_en_cajas != null
                ? `Cajas abiertas: ${formatMoney(panel.efectivo_en_cajas, formato)} · Resguardo: ${formatMoney(
                    String(toNumber(panel.efectivo_esperado) - toNumber(panel.efectivo_en_cajas)),
                    formato,
                  )}`
                : "En resguardo · sin cajas abiertas"
            }
          />
        ) : (
          <CifraDelDia
            rotulo="Esta semana"
            valor={formatMoney(panel.total_semana, formato)}
            icono={<Coins size={16} />}
            detalle={`Este mes: ${formatMoney(panel.total_mes, formato)}`}
          />
        )}

        {/* La ganancia solo llega con permiso; si no, se enseña el mes. */}
        {panel.ganancia_hoy !== null ? (
          <CifraDelDia
            rotulo="Ganancia de hoy"
            valor={formatMoney(panel.ganancia_hoy, formato)}
            tono={toNumber(panel.ganancia_hoy) >= 0 ? "exito" : "peligro"}
            icono={<TrendingUp size={16} />}
            detalle={
              // ⚠️ Sin ventas NO hay margen, y decir «0,0 %» es mentir: un cero
              // se lee como «te quedaste igual» cuando lo que pasa es que no
              // hay sobre qué calcularlo.
              panel.margen_hoy != null
                ? `Te queda el ${formatPercent(panel.margen_hoy)} de lo que vendes`
                : "Todavía sin ventas hoy"
            }
          />
        ) : (
          <CifraDelDia
            rotulo="Este mes"
            valor={formatMoney(panel.total_mes, formato)}
            icono={<Coins size={16} />}
            detalle={`Esta semana: ${formatMoney(panel.total_semana, formato)}`}
          />
      )}
    </div>
  );
}

/** La gráfica del día y lo más vendido. El detalle, para quien quiera más. */
function DetalleDelDia({
  panel,
  monedas: listaMonedas,
}: {
  panel: import("@/lib/types").Panel;
  monedas: import("@/lib/types").TenantCurrency[];
}) {
  const principal = monedaPrincipal(listaMonedas);
  const formato = principal ? comoFormato(principal) : MONEDA_POR_DEFECTO;

  const todasLasHoras = panel.ultimas_horas.map((h) => ({
    etiqueta: `${h.nombre}h`,
    valor: h.total,
  }));
  const primeraConVenta = todasLasHoras.findIndex((h) => toNumber(h.valor) > 0);
  const horas = primeraConVenta <= 0 ? todasLasHoras : todasLasHoras.slice(primeraConVenta);
  const vaLaGrafica = horas.length > 2 && toNumber(panel.total_hoy) > 0;

  const hayTop = panel.top_hoy.length > 0;
  if (!vaLaGrafica && !hayTop) return null;

  return (
    /*
      ⚠️ DOS COLUMNAS SOLO SI HAY DOS COSAS.

      Con `lg:grid-cols-2` fijo, un día sin gráfica dejaba «Lo que más se
      vendió» encogido a media pantalla con un hueco vacío al lado. Una tarjeta
      sola tiene que ocupar lo suyo: el hueco no es diseño, es un descuido que
      se nota.
    */
    <div className={cx("grid gap-4", vaLaGrafica && hayTop && "lg:grid-cols-2")}>
          {vaLaGrafica && (
            <Tarjeta>
              <TituloSeccion
                titulo="Las últimas horas"
                descripcion="Cómo se ha movido el día."
              />
              <BarrasVerticales datos={horas} moneda={formato} alto={90} />
            </Tarjeta>
          )}

          {panel.top_hoy.length > 0 && (
            <Tarjeta>
              <TituloSeccion
                titulo="Lo que más se vendió hoy"
                accion={
                  <Link
                    to="/reportes"
                    className="text-xs font-medium text-accent hover:underline"
                  >
                    Ver reportes
                  </Link>
                }
              />
              <div className="divide-y divide-line">
                {panel.top_hoy.map((i) => (
                  <div key={i.item_id} className="flex items-center gap-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{i.nombre}</span>
                    <span className="shrink-0 text-xs text-muted">
                      {toNumber(i.unidades)} {i.tipo === "service" ? "serv." : "u."}
                    </span>
                    <span className="tabular shrink-0 text-sm font-semibold text-ink">
                      {formatMoney(i.total, formato)}
                    </span>
                  </div>
                ))}
              </div>
            </Tarjeta>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function saludo(): string {
  const hora = new Date().getHours();
  if (hora < 12) return "Buenos días";
  if (hora < 19) return "Buenas tardes";
  return "Buenas noches";
}

function Cifra({
  icono,
  etiqueta,
  valor,
  a,
}: {
  icono: React.ReactNode;
  etiqueta: string;
  valor: number;
  a: string;
}) {
  return (
    <Link
      to={a}
      className="flex items-center gap-3.5 rounded-[var(--radius-card)] border border-line bg-surface p-4 shadow-[var(--shadow-card)] transition-colors hover:border-accent/40 hover:bg-surface-hover"
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-accent-soft text-accent">
        {icono}
      </div>
      <div className="min-w-0">
        <p className="tabular text-xl font-semibold leading-tight text-ink">{valor}</p>
        <p className="text-xs text-muted">{etiqueta}</p>
      </div>
    </Link>
  );
}

function Paso({
  hecho,
  titulo,
  descripcion,
  a,
  visible = true,
  descartar,
}: {
  hecho: boolean;
  titulo: string;
  descripcion: string;
  a: string;
  visible?: boolean;
  /**
   * Para los pasos que un negocio puede no necesitar nunca.
   *
   * Sin esto, una barbería de una sola persona ve para siempre un "Invita a tu
   * equipo" sin marcar. Una lista de tareas que nunca se puede terminar deja de
   * ser una guía y pasa a ser un reproche.
   */
  descartar?: { texto: string; onDescartar: () => void };
}) {
  if (!visible) return null;

  return (
    <div className="flex items-start gap-3.5 py-3.5">
      <div
        className={cx(
          "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2",
          hecho ? "border-success bg-success text-white" : "border-line-strong",
        )}
      >
        {hecho && <Check size={12} strokeWidth={3} />}
      </div>
      <div className="min-w-0 flex-1">
        <Link to={a} className="block transition-opacity hover:opacity-80">
          <p className={cx("text-sm font-medium", hecho ? "text-muted line-through" : "text-ink")}>
            {titulo}
          </p>
          <p className="mt-0.5 text-xs text-muted">{descripcion}</p>
        </Link>

        {!hecho && descartar && (
          <button
            type="button"
            onClick={descartar.onDescartar}
            className="mt-1.5 text-xs font-medium text-muted underline underline-offset-2 transition-colors hover:text-ink"
          >
            {descartar.texto}
          </button>
        )}
      </div>
      <Link to={a} aria-hidden tabIndex={-1}>
        <ArrowRight size={16} className="mt-1 shrink-0 text-faint" />
      </Link>
    </div>
  );
}
